import { Banknote, DollarSign, Globe2, Users, Heart, Landmark, PartyPopper, HandCoins } from "lucide-react";
import { SectionHeading, ngn, usd, EMPTY_BUCKET } from "./ui";

const STREAMS = [
  { key: "rentals_ngn", label: "Rentals · ₦ Nigerian", icon: Banknote, cur: "ngn" },
  { key: "rentals_african_usd", label: "Rentals · $ African", icon: Globe2, cur: "usd" },
  { key: "rentals_diaspora_usd", label: "Rentals · $ Diaspora", icon: DollarSign, cur: "usd" },
  { key: "watch_party_ngn", label: "Watch Party · ₦", icon: PartyPopper, cur: "ngn" },
  { key: "watch_party_usd", label: "Watch Party · $", icon: Users, cur: "usd" },
  { key: "donations_ngn", label: "Donations · ₦", icon: Heart, cur: "ngn" },
  { key: "donations_usd", label: "Donations · $", icon: HandCoins, cur: "usd" },
];

const Row = ({ label, value, tone = "text-foreground", testId, big = false }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-[11px] uppercase tracking-wider text-foreground/50">{label}</span>
    <span className={`font-display font-black tabular-nums ${big ? "text-2xl" : "text-base"} ${tone}`} data-testid={testId}>{value}</span>
  </div>
);

const Triple = ({ fmt, gross, creator, platform, testId, big }) => (
  <div className="space-y-2">
    <Row label="Gross" value={fmt(gross)} testId={`${testId}-gross`} big={big} />
    <Row label="Filmmakers 70%" value={fmt(creator)} tone="text-emerald-300" testId={`${testId}-creator`} big={big} />
    <Row label="Platform 30%" value={fmt(platform)} tone="text-gold" testId={`${testId}-platform`} big={big} />
  </div>
);

const StreamCard = ({ stream, bucket }) => {
  const fmt = stream.cur === "usd" ? usd : ngn;
  const Icon = stream.icon;
  const testId = `revenue-${stream.key.replace(/_/g, "-")}`;
  return (
    <div className="p-5 rounded-xl bg-surface border border-white/10 hover:border-white/20 transition-colors" data-testid={testId}>
      <div className="flex items-start justify-between gap-2 mb-4 text-foreground/60">
        <span className="text-xs uppercase tracking-wider font-semibold">{stream.label}</span>
        <Icon className="w-4 h-4 shrink-0" />
      </div>
      <Triple fmt={fmt} gross={bucket.gross} creator={bucket.creator ?? bucket.gross - bucket.platform} platform={bucket.platform} testId={testId} />
      <p className="text-[11px] text-foreground/40 mt-3 pt-3 border-t border-white/5">{bucket.count} paid transaction{bucket.count === 1 ? "" : "s"}</p>
    </div>
  );
};

const TotalCard = ({ title, fmt, totals, suffix, hint, testId }) => (
  <div className="p-6 rounded-xl bg-gradient-to-br from-terracotta/20 via-gold/5 to-transparent border border-terracotta/40" data-testid={testId}>
    <div className="flex items-center justify-between mb-4 text-foreground/60">
      <span className="text-xs uppercase tracking-wider font-semibold">{title}</span>
      <Landmark className="w-4 h-4" />
    </div>
    <Triple big fmt={fmt} gross={totals[`gross_${suffix}`]} creator={totals[`creator_${suffix}`]} platform={totals[`platform_${suffix}`]} testId={testId} />
    <p className="text-[11px] text-foreground/45 mt-3">{hint}</p>
  </div>
);

export default function RevenueSummary({ revenue }) {
  const r = revenue || {};
  const totals = { gross_naira: 0, creator_naira: 0, platform_naira: 0, gross_usd_cents: 0, creator_usd_cents: 0, platform_usd_cents: 0, ...(r.totals || {}) };
  return (
    <section data-testid="revenue-summary">
      <SectionHeading eyebrow="Platform revenue" title="Revenue by stream" description="Gross paid by viewers, the 70% paid out to filmmakers and Cinevoya's 30%. Currencies stay separate and are never converted." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <TotalCard title="All streams · ₦" fmt={ngn} totals={totals} suffix="naira" hint="₦ rentals + Watch Party ₦ + donations ₦" testId="revenue-total-ngn" />
        <TotalCard title="All streams · $" fmt={usd} totals={totals} suffix="usd_cents" hint="African $ + Diaspora $ rentals + Watch Party $ + donations $" testId="revenue-total-usd" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {STREAMS.map((s) => <StreamCard key={s.key} stream={s} bucket={{ ...EMPTY_BUCKET, ...(r[s.key] || {}) }} />)}
      </div>
    </section>
  );
}
